import { useRef, useEffect, useState } from "react";
import { Captions, Loader2, Pause, Play } from "lucide-react";

import { Button } from "../ui/button";
import api from "../../services/api";
import { lerPreferencia, gravarPreferencia } from "../../services/preferencias";

const LS_NAME = "audioMessageRate";
const VELOCIDADES = [1, 1.5, 2, 0.5];

// O que dizer quando a transcrição falha. A frase do backend é um código
// (`ERR_AUDIO_FORMAT_NOT_SUPPORTED: .amr`), e quem atende não tem que decifrar
// código — mas o formato importa, porque explica por que ESTE áudio não vai.
const RECADO_DO_ERRO = (bruto) => {
	const texto = String(bruto || "");

	if (texto.includes("ERR_AUDIO_FORMAT_NOT_SUPPORTED")) {
		const formato = texto.split(":").pop()?.trim();
		return `Este formato de áudio${formato ? ` (${formato})` : ""} não pode ser transcrito.`;
	}

	if (texto.includes("ERR_AUDIO_FILE_NOT_FOUND")) {
		return "O arquivo do áudio não está mais no servidor.";
	}

	if (texto.includes("ERR_NO_AI_PROVIDER_CONFIGURED")) {
		return "Nenhum provedor de IA configurado. Configure em Talk.Ai para transcrever.";
	}

	return "Não foi possível transcrever agora. Tente de novo.";
};

const tempo = segundos => {
	if (!Number.isFinite(segundos)) return "0:00";
	const m = Math.floor(segundos / 60);
	const s = Math.floor(segundos % 60);
	return `${m}:${String(s).padStart(2, "0")}`;
};

export default function Audio({
	url,
	messageId,
	transcricao,
	transcritoEm,
	podeTranscrever = false
}) {
	const audioRef = useRef(null);
	const [audioRate, setAudioRate] = useState(
		parseFloat(lerPreferencia(LS_NAME, "1")) || 1
	);
	const [tocando, setTocando] = useState(false);
	const [posicao, setPosicao] = useState(0);
	const [duracao, setDuracao] = useState(0);

	// `transcritoEm` é o que separa "ninguém pediu ainda" de "pedimos e o áudio
	// não tinha fala" — a mesma distinção que o backend guarda em
	// `transcribedAt`. Sem ela, áudio mudo pareceria botão quebrado.
	const [texto, setTexto] = useState(transcricao || "");
	const [jaTranscrito, setJaTranscrito] = useState(Boolean(transcritoEm));
	const [transcrevendo, setTranscrevendo] = useState(false);
	const [erro, setErro] = useState("");

	const transcrever = async () => {
		if (transcrevendo || !messageId) return;

		setTranscrevendo(true);
		setErro("");

		try {
			const { data } = await api.post(`/messages/${messageId}/transcribe`);
			setTexto(data?.transcription || "");
			setJaTranscrito(true);
		} catch (err) {
			// Inline, não em toast: o aviso pertence a ESTE áudio, e um toast
			// solto no canto não diz de qual mensagem está falando.
			setErro(RECADO_DO_ERRO(err?.response?.data?.error || err?.message));
		}

		setTranscrevendo(false);
	};

	useEffect(() => {
		audioRef.current.playbackRate = audioRate;
		gravarPreferencia(LS_NAME, audioRate);
	}, [audioRate]);

	const alternarVelocidade = () => {
		const atual = VELOCIDADES.indexOf(audioRate);
		setAudioRate(VELOCIDADES[(atual + 1) % VELOCIDADES.length] || 1);
	};

	const alternarPlay = () => {
		const player = audioRef.current;
		if (player.paused) player.play();
		else player.pause();
	};

	// Enquanto os metadados não chegam, `duration` é NaN: o range fica com
	// max=0 e a bolinha não anda. Usa a posição atual como piso.
	const max = duracao || posicao || 1;

	const mostrarBotao = podeTranscrever && messageId && !jaTranscrito;

	return (
		<div className="w-[260px] max-w-full">
		<div className="flex items-center gap-2 rounded-full bg-muted/60 px-2 py-1.5">
			<audio
				ref={audioRef}
				src={url}
				preload="metadata"
				onPlay={() => setTocando(true)}
				onPause={() => setTocando(false)}
				onEnded={() => setTocando(false)}
				onTimeUpdate={e => setPosicao(e.target.currentTime)}
				onLoadedMetadata={e => setDuracao(e.target.duration)}
				onDurationChange={e => setDuracao(e.target.duration)}
			/>
			<Button
				variant="ghost"
				size="icon"
				className="size-8 shrink-0 rounded-full bg-background text-foreground hover:bg-background/80"
				onClick={alternarPlay}
				aria-label={tocando ? "Pausar áudio" : "Tocar áudio"}
			>
				{tocando ? (
					<Pause className="size-4 fill-current" />
				) : (
					<Play className="size-4 fill-current" />
				)}
			</Button>

			<div className="flex min-w-0 flex-1 flex-col gap-0.5">
				{/* range nativo: o `accent-primary` já pinta a parte tocada.
				    Estilizar o thumb à mão (appearance-none) desliga esse
				    preenchimento e a barra fica toda cinza. */}
				<input
					type="range"
					min={0}
					max={max}
					step="any"
					value={Math.min(posicao, max)}
					onChange={e => {
						const v = Number(e.target.value);
						audioRef.current.currentTime = v;
						setPosicao(v);
					}}
					aria-label="Posição do áudio"
					className="h-1 w-full cursor-pointer accent-primary"
				/>
				<span className="text-[11px] tabular-nums text-muted-foreground">
					{tempo(posicao)} / {tempo(duracao)}
				</span>
			</div>

			<Button
				variant="ghost"
				size="sm"
				className="h-6 shrink-0 rounded-full px-1.5 text-[11px] tabular-nums"
				onClick={alternarVelocidade}
				aria-label="Velocidade de reprodução"
			>
				{audioRate}x
			</Button>

			{mostrarBotao && (
				<Button
					variant="ghost"
					size="icon"
					className="size-6 shrink-0 rounded-full"
					onClick={transcrever}
					disabled={transcrevendo}
					title="Ler o áudio em texto"
					aria-label="Transcrever áudio"
				>
					{transcrevendo ? (
						<Loader2 className="size-3.5 animate-spin" />
					) : (
						<Captions className="size-3.5" />
					)}
				</Button>
			)}
		</div>

		{/* O texto fica FORA da pílula do player, abaixo dela: dentro, um áudio
		    de dois minutos empurraria os controles para fora da bolha. */}
		{jaTranscrito && (
			<div className="mt-1 rounded-lg bg-muted/60 px-2 py-1.5">
				<p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
					Transcrição
				</p>
				<p className="wrap-break-word whitespace-pre-wrap pt-0.5 text-sm">
					{texto || (
						<span className="italic text-muted-foreground">
							Nenhuma fala reconhecida neste áudio.
						</span>
					)}
				</p>
			</div>
		)}

		{erro && (
			<p role="status" className="mt-1 text-xs text-destructive">
				{erro}
			</p>
		)}
		</div>
	);
}
